<template>
	<div ref="bio" class="pilot-modal">
		<div class="pilot-header-container">
			<div class="section-header clipped-fit-backward">
				<img src="/icons/pilot.svg" />
				<h1>{{ pilot.name || "UNIDENTIFIED" }} [{{ pilot.callsign }}]</h1>
			</div>
			<div class="rhombus-back">&nbsp;</div>
		</div>
		<div class="pilot">
			<div class="bio-scroll">
				<!-- Bio sections from the pilot file; empty headings are skipped -->
				<section v-for="section in bioSections" :key="section.title" class="bio-section">
					<h2 class="bio-heading">{{ section.title }}</h2>
					<vue-markdown-it
						v-if="section.body"
						:source="section.body"
						preset="default"
						:options="{ html: true }"
						class="bio-body"
					/>
					<p v-else class="bio-missing">[NO RECORD ON FILE]</p>
				</section>

				<section v-if="pilot.bond" class="bio-section">
					<h2 class="bio-heading">Bond // {{ pilot.bond.name }}</h2>
					<div v-if="pilot.bond.majorIdeals.length" class="bond-block">
						<span class="bond-label">Major Ideals</span>
						<ul class="bond-ideals">
							<li v-for="ideal in pilot.bond.majorIdeals" :key="ideal">{{ ideal }}</li>
						</ul>
					</div>
					<div v-if="pilot.bond.minorIdeal" class="bond-block">
						<span class="bond-label">Minor Ideal</span>
						<p class="bond-text">{{ pilot.bond.minorIdeal }}</p>
					</div>
					<div v-for="qa in pilot.bond.answers" :key="qa.question + qa.answer" class="bond-block">
						<span class="bond-label">{{ qa.question || "Answer" }}</span>
						<p class="bond-text">{{ qa.answer }}</p>
					</div>
				</section>
			</div>
		</div>
	</div>
	<div ref="art" class="pilot-modal portrait">
		<div class="pilot-header-container">
			<div class="section-header clipped-medium-backward-pilot">
				<img src="/icons/portrait.svg" />
				<h1>Pilot Artwork</h1>
			</div>
			<div class="rhombus-back">&nbsp;</div>
		</div>
		<div class="pilot">
			<img :src="pilot.portrait" class="portrait" @error="e => (e.target.src = './nodata.png')" />
		</div>
	</div>
</template>

<script>
import { VueMarkdownIt } from "@f3ve/vue-markdown-it";
import { animate, stagger } from "motion";
import { reducedMotion } from "@/utils/retrieve";

const EASE_OUT = [0.22, 1, 0.36, 1];

export default {
	components: {
		VueMarkdownIt,
	},
	inheritAttrs: false,
	// Opening: the backdrop dims while both panels unfold open from their centre line
	// (solid all the way, so nothing shows through), then their contents settle in just
	// behind and the portrait scans in
	mounted() {
		if (reducedMotion()) return;
		const panels = [this.$refs.bio, this.$refs.art].filter(Boolean);
		const overlay = panels[0]?.closest(".o-modal")?.querySelector(".o-modal__overlay");
		if (overlay) animate(overlay, { opacity: [0, 1] }, { duration: 0.25 });
		const unclip = el => setTimeout(() => (el.style.clipPath = ""), 50);
		// The header bar sits just outside each panel's box, so it wipes across on its own
		// while the panel body unfolds
		panels.forEach((panel, i) => {
			// The header (clipped bar + slanted piece) wipes in as one unit. The bar sits
			// ~38px above its container's box, so the reveal extends upward to include it
			const header = panel.querySelector(".pilot-header-container");
			const body = panel.querySelector(".pilot");
			const at = i * 0.06;
			if (header)
				animate(
					header,
					{ clipPath: ["inset(-60px 100% -10px 0)", "inset(-60px 0% -10px 0)"] },
					{ duration: 0.45, delay: at, ease: EASE_OUT }
				).finished.then(() => unclip(header));
			if (body)
				animate(
					body,
					{ clipPath: ["inset(50% 0 50% 0)", "inset(0% 0 0% 0)"] },
					{ duration: 0.42, delay: i * 0.06, ease: EASE_OUT }
				).finished.then(() => unclip(body));
		});
		const bio = this.$refs.bio;
		if (bio)
			animate(
				bio.querySelectorAll(".bio-section"),
				{ opacity: [0, 1], y: [8, 0] },
				{ duration: 0.3, delay: stagger(0.04, { startDelay: 0.18 }), ease: EASE_OUT }
			);
		const img = this.$refs.art?.querySelector(".portrait");
		if (img)
			animate(
				img,
				{ clipPath: ["inset(0 0 100% 0)", "inset(0 0 0% 0)"] },
				{ duration: 0.5, delay: 0.25, ease: "easeOut" }
			).finished.then(() => unclip(img));
	},
	props: {
		pilot: {
			type: Object,
			required: true,
		},
	},
	computed: {
		// Split the Markdown bio on "## Heading" lines. Appearance and History always
		// show (flagged when empty, like the card's error stamps); other empty
		// sections are dropped.
		bioSections() {
			const text = String(this.pilot.bio || "").trim();
			const parts = text.split(/^##\s+/m);
			const found = [];
			const intro = parts.shift().trim();
			if (intro) found.push({ title: "Record", body: intro });
			parts.forEach(part => {
				const newline = part.indexOf("\n");
				const title = (newline === -1 ? part : part.slice(0, newline)).trim();
				const body = newline === -1 ? "" : part.slice(newline + 1).trim();
				found.push({ title, body });
			});
			const required = ["Appearance", "History"].map(
				title =>
					found.find(s => s.title.toLowerCase() === title.toLowerCase()) || { title, body: "" }
			);
			const extra = found.filter(
				s => s.body && !required.some(r => r.title.toLowerCase() === s.title.toLowerCase())
			);
			return [...required, ...extra];
		},
	},
};
</script>
<style scoped>
.bio-scroll {
	height: 100%;
	overflow-y: auto;
	padding: 1.4em 2em 1.6em;
	box-sizing: border-box;
}

.bio-section + .bio-section {
	margin-top: 1.6em;
	padding-top: 1.2em;
	border-top: 1px solid rgba(209, 200, 184, 0.12);
}

.bio-heading {
	margin: 0 0 0.6em;
	font-family: "Big Shoulders Display", cursive;
	font-size: 22px;
	font-weight: 800;
	letter-spacing: 0.1em;
	text-transform: uppercase;
	color: var(--text-mission-status);
}

.bio-body {
	font-family: "Titillium Web", sans-serif;
	font-size: 15px;
	line-height: 1.55;
	color: var(--text-markdown-p);
}

.bio-body :deep(p) {
	margin: 0 0 0.8em;
}

.bond-block {
	margin-bottom: 0.9em;
}

.bond-label {
	display: block;
	margin-bottom: 0.3em;
	font-family: "Raleway", sans-serif;
	font-size: 10px;
	font-weight: 800;
	letter-spacing: 2.5px;
	text-transform: uppercase;
	color: #b8973a;
}

.bond-ideals {
	margin: 0;
	padding-left: 1.2em;
	font-family: "Titillium Web", sans-serif;
	font-size: 15px;
	line-height: 1.5;
	color: var(--text-markdown-p);
}

.bond-text {
	margin: 0;
	font-family: "Titillium Web", sans-serif;
	font-size: 15px;
	line-height: 1.5;
	color: var(--text-markdown-p);
}

.bio-missing {
	margin: 0;
	font-family: "Share Tech Mono", monospace;
	font-size: 13px;
	letter-spacing: 0.15em;
	color: #e0566b;
}
</style>
